import {
	awaitingLocations,
	awaitingPinnedLocation,
	locationsRepository,
	pinnedLocation
} from '../components/bootstrap/location/bootstrap';
import { ICoords, ILocationEntity } from '../types/location.types.d';
import { sortByDistance, sortBySelected, delayMe } from '../components/bootstrap/location/methods';
import { dispatch, listen } from '@city-mattress/js-utils';
import { LocationEntity } from '../components/bootstrap/location/location-entity';
import { EVENTS } from '../components/bootstrap/location/constants';

const LOOKUP_EVENT_NAME = 'CM_GEO_FLYOUT';

document.addEventListener('alpine:init', () => {
	const Alpine = window['Alpine'];

	Alpine.data('preferredStore', () => ({
		userPinnedLocation: <ILocationEntity>null,
		init() {
			listen(EVENTS.LOCATION_WAS_SELECTED, (event: CustomEvent) => {
				this.userPinnedLocation = <ILocationEntity>event.detail;
			});

			// Hydrate from the same bootstrap as locationsFlyout so the header does not
			// depend on the store flyout (async x-load off-canvas) having run first.
			Promise.all([awaitingPinnedLocation, awaitingLocations])
				.then((values) => {
					if (this.userPinnedLocation) {
						return;
					}
					const pin: ILocationEntity | boolean = values[0];
					const locations: ILocationEntity[] = values[1];

					if (pin instanceof LocationEntity) {
						this.userPinnedLocation = pin;
					} else if (locations.length) {
						const sorted = [...locations].sort(sortByDistance);
						this.userPinnedLocation = sorted[0];
					}
				})
				.catch((error: unknown) => {
					console.error('Failed to initialise preferred store header', error);
				});
		}
	}));

	Alpine.data('locationsFlyout', () => ({
		list: <ILocationEntity[]>[],
		userPinnedLocation: <ILocationEntity>null,
		userLocation: <ICoords>null,
		highlightedLocation: <number>null,
		loading: true,
		selectedIndex: null,
		searchStatusMessage: '',
		isPinned(location: ILocationEntity) {
			return location.storisId === this.userPinnedLocation?.storisId;
		},
		isHighlighted(location: ILocationEntity) {
			return location.storisId === this.highlightedLocation;
		},
		pinLocation(location: ILocationEntity) {
			this.userPinnedLocation = location;
			this.highlightedLocation = null;

			dispatch(EVENTS.LOCATION_WAS_SELECTED, this.userPinnedLocation);

			//save for next load
			pinnedLocation.persistPin(location);

			this.loading = true;
			delayMe(() => {
				sortBySelected(this.list, this.userPinnedLocation);
				this.loading = false;
			});
		},
		assertHighlight(location: ILocationEntity) {
			return this.highlightedLocation === location.storisId;
		},
		assertMyLocationHoursShortMessage(currentTime) {
			return this.userPinnedLocation && this.userPinnedLocation.storeOpensOrCloses;
		},
		//highlights a temporary location
		highlight(location: ILocationEntity) {
			if (this.highlightedLocation !== location.storisId) {
				this.highlightedLocation = location.storisId;
			} else {
				// Toggle off if already highlighted
				this.highlightedLocation = null;
			}
		},

		/**
		 * Announces search results to screen readers and focuses first result
		 */
		announceSearchResults(locations: ILocationEntity[]) {
			const count = locations.length;
			if (count === 0) {
				this.searchStatusMessage = 'No stores found. Try a different search.';
			} else {
				const storeText = count === 1 ? 'store' : 'stores';
				this.searchStatusMessage = `${count} ${storeText} found. Use Tab or arrow keys to navigate.`;
			}

			// Focus the first store's radio button after a short delay to allow DOM update
			setTimeout(() => {
				const firstStore = locations[0];
				if (firstStore) {
					const firstRadio = document.querySelector(`#StoreRadio${firstStore.storisId}`) as HTMLElement;
					if (firstRadio) {
						firstRadio.focus();
					}
				}
			}, 100);
		},
		init() {
			listen(LOOKUP_EVENT_NAME, (event) => {
				const coords: ICoords = event.detail;
				this.loading = true;
				locationsRepository.getByDistance(coords).then((locations: ILocationEntity[]) => {
					this.list = [...locations];

					delayMe(() => {
						this.loading = false;
						// Announce results and focus first item
						this.announceSearchResults(locations);
					});
				}).catch((error: unknown) => {
					console.error('Failed to get locations by distance', error);
					this.loading = false;
				});
			});

			Promise.all([awaitingPinnedLocation, <Promise<[]>>awaitingLocations])
				.then((values) => {
					const userPinnedLocation: ILocationEntity | boolean = values[0];

					const locations: ILocationEntity[] = values[1];

					if (userPinnedLocation instanceof LocationEntity) {
						this.userPinnedLocation = <LocationEntity>userPinnedLocation;
						this.list = <ILocationEntity[]>locations;
						sortBySelected(this.list, this.userPinnedLocation);
					} else {
						this.list = <ILocationEntity[]>locations;
						this.list.sort(sortByDistance);

						//grab the closest store to the user
						this.userPinnedLocation = this.list[0];
					}

					dispatch(EVENTS.LOCATION_WAS_SELECTED, <ILocationEntity>this.userPinnedLocation);

					return this;
				})
				.then((instance) => {
					delayMe(() => {
						this.loading = false;
					});
				})
				.catch((error: unknown) => {
					console.error('Failed to initialise locations flyout', error);
					this.loading = false;
				});
		}
	}));
});
